import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { investmentPerspectivesAPI } from '../../services/api';
import LayoutEditor from './LayoutEditor';
import AddBlockModal from './AddBlockModal';
import PropagateModal from './PropagateModal';
import { BLOCK_REGISTRY, DEFAULT_LAYOUT, getBlockDef } from './BlockRenderer';
import useReportEditor from './useReportEditor';
import ColourPaletteEditor from './ColourPaletteEditor';

const EditableContentPanel = forwardRef(({ content, portfolioCode, portfolioName, quarter, branding, onSave, onChange, brands, domain, overflowBlocks, onLayoutModeChange }, ref) => {
  // ── Centralised state via useReportEditor hook (useReducer + undo/redo) ──
  const {
    formData, canUndo, canRedo,
    setField, setLayout, addBlock, removeBlock,
    addPage, removePage, setCustomContent, setHeading, setHeadingStyle, setBlockDef, setPageBlocks,
    togglePageFooter, reorderBlock,
    resetAll, undo, redo,
  } = useReportEditor(content, onChange);

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('editor');
  const [addBlockTarget, setAddBlockTarget] = useState(null); // page index or null
  const [showPropagate, setShowPropagate] = useState(false);

  // ── Copy-from-month (same domain) ──
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  const [copyMonths, setCopyMonths] = useState([]);
  const [copySource, setCopySource] = useState('');
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!showCopyFrom) return;
    investmentPerspectivesAPI.getAvailableMonths()
      .then((res) => {
        // Report months start July 2026; exclude the month being copied into
        const opts = (res.months || []).map((m) => m.value)
          .filter((m) => m >= '2026-07' && m !== quarter)
          .sort()
          .reverse();
        setCopyMonths(opts);
        // Default to the closest earlier month (the usual "last month" case)
        setCopySource(opts.find((m) => m < quarter) || opts[0] || '');
      })
      .catch(() => setCopyMonths([]));
  }, [showCopyFrom, quarter]);

  const handleCopyFrom = async () => {
    if (!copySource) return;
    setCopying(true);
    try {
      await investmentPerspectivesAPI.copyFrom(quarter, copySource, domain || '_default');
      // Copied content is now the saved state — reload so the editor picks it up
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Error copying content' });
      setCopying(false);
      setShowCopyFrom(false);
    }
  };

  // Expose editor actions to parent for inline layout + text editing on the preview
  useImperativeHandle(ref, () => ({
    handleBlockChange: (pageIndex, blockId, updatedBlock) => {
      const layout = formData.page_layout || DEFAULT_LAYOUT;
      const newBlocks = layout[pageIndex]?.blocks.map(b =>
        (typeof b === 'string' ? b : b.id) === blockId ? updatedBlock : b
      );
      if (newBlocks) handlePageBlocksChange(pageIndex, newBlocks);
    },
    // Batch variant — apply many block updates in ONE dispatch. Looping over
    // handleBlockChange rebuilds from the same stale formData snapshot each
    // call, so only the last block's change would survive (Autofit All).
    handleBlocksChange: (pageIndex, updatesById) => {
      const layout = formData.page_layout || DEFAULT_LAYOUT;
      const newBlocks = layout[pageIndex]?.blocks.map(b => {
        const id = typeof b === 'string' ? b : b.id;
        return updatesById[id] || b;
      });
      if (newBlocks) handlePageBlocksChange(pageIndex, newBlocks);
    },
    handleRemoveBlock: removeBlock,
    handleAddBlock: (pageIndex) => setAddBlockTarget(pageIndex),
    togglePageFooter,
    reorderBlock,
    setField,
    setCustomContent,
    setHeading,
    setHeadingStyle,
    setBlockDef,
    formData,
  }), [formData, removeBlock, setPageBlocks, setAddBlockTarget, setField, setCustomContent, setHeading, setHeadingStyle, setBlockDef]);

  // Layout mode always active when editor panel is visible
  useEffect(() => {
    if (onLayoutModeChange) {
      onLayoutModeChange(true);
    }
    return () => {
      if (onLayoutModeChange) onLayoutModeChange(false);
    };
  }, [onLayoutModeChange]);

  // Version history state
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);

  // Keyboard shortcut for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Wrapped handlers that delegate to the hook
  const handleChange = useCallback((key, value) => setField(key, value), [setField]);
  const handleLayoutChange = useCallback((newLayout) => setLayout(newLayout), [setLayout]);
  const handleAddBlock = useCallback((pageIndex, blockId, blockDef, imageData) => addBlock(pageIndex, blockId, blockDef, imageData), [addBlock]);
  const handleRemoveBlock = useCallback((blockId) => removeBlock(blockId), [removeBlock]);
  const handleAddPage = useCallback(() => addPage(), [addPage]);
  const handleRemovePage = useCallback((pageIndex) => removePage(pageIndex), [removePage]);
  const handleCustomContentChange = useCallback((blockId, html) => setCustomContent(blockId, html), [setCustomContent]);
  const handlePageBlocksChange = useCallback((pageIdx, newBlocks) => setPageBlocks(pageIdx, newBlocks), [setPageBlocks]);

  const handleDeleteAllContent = useCallback(async () => {
    if (!window.confirm('Delete all content and reset this report to a blank layout? This will save immediately.')) return;
    const blankData = resetAll();
    try {
      await onSave(blankData);
      setMessage({ type: 'success', text: 'All content deleted — report reset to blank' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg).join('; ')
        : 'Error resetting content';
      setMessage({ type: 'error', text });
    }
  }, [onSave, resetAll]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(formData);
      setMessage({ type: 'success', text: 'Content saved successfully' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const text = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg).join('; ')
        : 'Error saving content';
      setMessage({ type: 'error', text });
    } finally {
      setSaving(false);
    }
  };

  // ── Version History Handlers ──
  const loadVersions = useCallback(async () => {
    if (!quarter) return;
    setVersionsLoading(true);
    try {
      const result = await investmentPerspectivesAPI.listVersions(quarter, domain || '_default');
      setVersions(result.versions || []);
    } catch (err) {
      console.error('Error loading version history:', err);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, [quarter, domain]);

  // Load versions when switching to the history tab
  useEffect(() => {
    if (activeTab === 'history') {
      loadVersions();
    }
  }, [activeTab, loadVersions]);

  const handleRestoreVersion = useCallback(async (versionId) => {
    if (!window.confirm('Restore this version? The current content will be saved as a new version before restoring.')) return;
    setRestoringVersion(versionId);
    try {
      await investmentPerspectivesAPI.restoreVersion(versionId);
      setMessage({ type: 'success', text: 'Version restored successfully. Reloading...' });
      // Reload the report content to reflect restored version
      if (onSave) {
        // Trigger a reload by re-fetching — the parent handles this via loadContentOnly
        window.location.reload();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Error restoring version' });
    } finally {
      setRestoringVersion(null);
    }
  }, [onSave]);

  return (
    <div className="editable-content-panel">
      {showPropagate && (
        <PropagateModal
          brands={brands}
          period={quarter}
          domain={domain}
          formData={formData}
          onClose={() => setShowPropagate(false)}
          onComplete={() => setShowPropagate(false)}
        />
      )}
      {showCopyFrom && (
        <div className="propagate-modal-overlay" onClick={() => !copying && setShowCopyFrom(false)}>
          <div className="propagate-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="propagate-modal-header">
              <h3>Copy From Month</h3>
              <button className="propagate-modal-close" onClick={() => setShowCopyFrom(false)} disabled={copying}>&times;</button>
            </div>
            <p className="propagate-modal-desc">
              Copies the whole report — layout, blocks, commentary, headings, title and
              colours — from the selected month into <strong>{quarter}</strong>
              {domain && domain !== '_default' ? ` for ${domain}` : ' (Drummond)'}.
              Data-driven tables and charts will show this month's numbers automatically.
              Existing {quarter} content is overwritten (a restore point is kept in History).
            </p>
            <div className="propagate-modal-controls">
              <label className="propagate-quarter-selector">
                <span>Copy from</span>
                <select value={copySource} onChange={(e) => setCopySource(e.target.value)} disabled={copying}>
                  {copyMonths.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            </div>
            <div className="propagate-modal-footer">
              <button className="btn-cancel" onClick={() => setShowCopyFrom(false)} disabled={copying}>Cancel</button>
              <button className="btn-propagate" onClick={handleCopyFrom} disabled={copying || !copySource}>
                {copying ? 'Copying...' : `Copy ${copySource || ''} → ${quarter}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="panel-sticky-header">
        <div className="panel-header">
          <h3>Edit Report</h3>
          <div className="panel-header-actions">
            <button
              className="btn-undo"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
            <button
              className="btn-redo"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Y)"
            >
              Redo
            </button>
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="btn-copy-from"
              onClick={() => setShowCopyFrom(true)}
              title="Copy another month's report into this month"
            >
              Copy From
            </button>
            {brands && brands.some((b) => b.tailored) && (
              <button
                className="btn-propagate-open"
                onClick={() => setShowPropagate(true)}
              >
                Propagate
              </button>
            )}
            <button
              className="btn-delete-all"
              onClick={handleDeleteAllContent}
            >
              Delete All
            </button>
          </div>
        </div>

        {/* Tab toggle */}
        <div className="editor-tab-bar">
          <button
            className={`editor-tab${activeTab === 'editor' ? ' active' : ''}`}
            onClick={() => setActiveTab('editor')}
          >
            Editor
          </button>
          <button
            className={`editor-tab${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button
            className={`editor-tab${activeTab === 'colours' ? ' active' : ''}`}
            onClick={() => setActiveTab('colours')}
          >
            Colours
          </button>
        </div>
        {message && (
          <div className={`panel-message ${message.type}`}>{message.text}</div>
        )}
      </div>

      {/* ── Editor Tab (Layout + Title + Disclaimer) ── */}
      {activeTab === 'editor' && (
        <>
          <LayoutEditor
            layout={formData.page_layout || DEFAULT_LAYOUT}
            onLayoutChange={handleLayoutChange}
            onAddBlock={(pageIndex) => setAddBlockTarget(pageIndex)}
            onRemoveBlock={handleRemoveBlock}
            onAddPage={handleAddPage}
            onRemovePage={handleRemovePage}
            customBlocks={formData.custom_blocks}
          />

          <AddBlockModal
            isOpen={addBlockTarget !== null}
            onClose={() => setAddBlockTarget(null)}
            onAdd={(blockId, blockDef, imageData) => handleAddBlock(addBlockTarget, blockId, blockDef, imageData)}
            currentLayout={formData.page_layout || DEFAULT_LAYOUT}
          />

          {/* Title Box */}
          <div className="content-page-divider">Title</div>
          {(() => {
            const titleData = formData.title_box || {};
            return (
              <div className="section-editor">
                <div className="title-box-fields">
                  {!branding && (
                    <div className="field-row">
                      <label className="field-label">Title Text</label>
                      <input
                        type="text"
                        className="field-input"
                        value={titleData.title_text || ''}
                        onChange={(e) => handleChange('title_box', { ...titleData, title_text: e.target.value })}
                        placeholder="Investment Perspectives (leave blank for default)"
                      />
                    </div>
                  )}
                  <div className="field-row">
                    <label className="field-label">Subtitle</label>
                    <input
                      type="text"
                      className="field-input"
                      value={titleData.subtitle || ''}
                      onChange={(e) => handleChange('title_box', { ...titleData, subtitle: e.target.value })}
                      placeholder="Leave blank for the month (e.g. July 2026)"
                    />
                  </div>
                  <div className="field-row">
                    <label className="field-label">Title Size (pt)</label>
                    <input
                      type="number"
                      min="10"
                      max="72"
                      className="field-input field-input-short"
                      value={titleData.title_size || ''}
                      onChange={(e) => handleChange('title_box', { ...titleData, title_size: e.target.value ? Number(e.target.value) : null })}
                      placeholder="auto"
                    />
                  </div>
                  <div className="field-row">
                    <label className="field-label">Subtitle Size (pt)</label>
                    <input
                      type="number"
                      min="6"
                      max="48"
                      className="field-input field-input-short"
                      value={titleData.subtitle_size || ''}
                      onChange={(e) => handleChange('title_box', { ...titleData, subtitle_size: e.target.value ? Number(e.target.value) : null })}
                      placeholder="20"
                    />
                  </div>
                  <div className="field-row">
                    <label className="field-label">Title Colour</label>
                    <input
                      type="color"
                      className="shape-colour-input"
                      value={titleData.title_colour || '#072040'}
                      onChange={(e) => handleChange('title_box', { ...titleData, title_colour: e.target.value })}
                      title="Title text colour"
                    />
                    <button
                      type="button"
                      className="modal-add-btn small"
                      style={{ marginLeft: 6 }}
                      onClick={() => handleChange('title_box', { ...titleData, title_colour: '#ffffff' })}
                    >
                      White
                    </button>
                    <button
                      type="button"
                      className="modal-add-btn small"
                      style={{ marginLeft: 6 }}
                      onClick={() => handleChange('title_box', { ...titleData, title_colour: null })}
                    >
                      Default
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

        </>
      )}

      {/* ── Colours Tab ── */}
      {activeTab === 'colours' && (
        <ColourPaletteEditor
          formData={formData}
          setField={handleChange}
          branding={branding}
        />
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="version-history-panel">
          <p className="version-history-info">
            Previous versions are automatically saved each time content is updated.
            Restoring a version will save the current content as a new version first.
          </p>
          {versionsLoading && (
            <div className="text-center" style={{ padding: '2rem' }}>
              <div className="loading-spinner" />
              <p style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '0.5rem' }}>Loading version history...</p>
            </div>
          )}
          {!versionsLoading && versions.length === 0 && (
            <p className="version-history-empty">No previous versions found. Versions are created each time you save.</p>
          )}
          {!versionsLoading && versions.length > 0 && (
            <div className="version-list">
              {versions.map((v, i) => (
                <div key={v.version_id} className="version-item">
                  <div className="version-meta">
                    <span className="version-date">
                      {v.saved_at ? new Date(v.saved_at).toLocaleString('en-AU', {
                        day: 'numeric', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      }) : 'Unknown date'}
                    </span>
                    <span className="version-user">{v.saved_by || 'Unknown'}</span>
                    {i === 0 && <span className="version-latest-badge">Most Recent</span>}
                  </div>
                  <button
                    className="btn-restore-version"
                    onClick={() => handleRestoreVersion(v.version_id)}
                    disabled={restoringVersion !== null}
                  >
                    {restoringVersion === v.version_id ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default EditableContentPanel;
